// 看更多 — the ranked pool of styles the engine did NOT propose.
//
// Magnus 2026-09-17, on the screen: 「建議加入 only have 2 items … i need more, show me suggestions
// and i will decide whether to add」. Measured the same day: 建議加入 is bounded by his own
// `new_styles_per_week` dial (8 → 2 proposals, 20 → 17, 30 → 19, where the evidence floor takes
// over), so the cap is the dial and not the catalogue. This hook fetches past it.
//
// Three properties this hook exists to guarantee, all of them hard-won on this screen:
//
//  1. NOTHING IN THE CLICK PATH AWAITS. 加入清單 / 不要這款 hide the row in the same frame and fire
//     the POST behind it, exactly as every other 選品 verb does. /restock-v2 is ~6.3s on prod.
//
//  2. AN ANSWER SURVIVES A RECOMPUTE LANDING. The pool keeps its OWN `decided` set rather than
//     reading the shared optimistic patch. That patch is retired when a plan that predates the
//     answer lands — correct for the plan, fatal here, because the pool is not refetched and its
//     `items` array still holds the row, so it would pop back on screen. That is the wholesale-clear
//     bug (「why it reverted back」) on a new surface. `decided` is never cleared by a landing plan.
//
//  3. PAGINATION IS NOT COLLATERAL DAMAGE. The pool is a separate endpoint; a plan recompute never
//     touches `items`, `nextOffset` or `total`, so a refresh landing mid-scroll costs him nothing.
//
// What is deliberately NOT here: any batch verb. Every row in this pool is below the engine's
// evidence bar, and a bulk accept over rows nothing vouched for is the 2026-09-15 mistake (16
// evidence-free styles into a Korea order) with a friendlier button. One tap, one style.
import { useCallback, useRef, useState } from 'react';
import api from '../../../services/api';
import { readNokeError } from './format';
import { POOL_BATCH, POOL_ERROR, mergePoolItems, poolEvidence, poolItems, poolRemaining } from './counterList';
import {
  CounterListDecision, PoolScope, PoolSeason, RestockV2ListPool, RestockV2ListPoolFacets,
  RestockV2ListPoolItem, TierKey,
} from './types';

/** One complete view of the pool. Every fetch takes the whole thing, so no axis can go stale. */
export interface PoolFilter {
  scope: PoolScope;
  season: PoolSeason | null;
  cat: string | null;
}

export const POOL_URL = '/api/noke/restock-v2/list/pool';

/** What the pool needs from 選品's write hook: the SAME /list/decide every proposal card uses. */
export type PoolDecide = (
  sku: string, decision: CounterListDecision, tier?: TierKey | null, reason?: string | null,
) => Promise<boolean>;

export interface CounterPool {
  // Closed until he asks. Open means 「rows are on screen」, not 「rows are loaded」 — priming is
  // separate, so the first tap never waits on the network.
  open: boolean;
  toggle: () => void;
  // Everything fetched so far, minus what he has answered. Deduped by SKU across batches.
  items: RestockV2ListPoolItem[];
  // Server total for the CURRENT view, minus his answers here. What 「還有 N 款」 prints.
  remaining: number;
  // Undecided ignoring the view filter — the number on the 全部 chip. 0 until the server sends one.
  totalAll: number;
  // The default cut's own total. Served on every response as `total_default` when the backend has
  // it; otherwise remembered from the last 比較有機會的 response, because the server reports `total`
  // only for the view it just served and a chip with no number is a filter he cannot price.
  totalRelevant: number;
  scope: PoolScope;
  // The two optional axes. null = 不分 (the param is omitted entirely — 'ALL' is a season VALUE,
  // 全季款, not 「no filter」).
  season: PoolSeason | null;
  cat: string | null;
  // Every filter change goes through ONE setter and restarts the list at offset 0. His ANSWERS
  // carry over; only the rows do not, because they are a different query.
  setFilter: (next: Partial<PoolFilter>) => void;
  clearFilter: () => void;
  filtered: boolean;
  // The rack groups to draw chips for, and the per-chip counts, both straight from the server.
  rackGroups: string[] | null;
  facets: RestockV2ListPoolFacets | null;
  hasMore: boolean;
  // First batch only — like the tab's `loading`, it is the one moment there is nothing to keep.
  loading: boolean;
  loadingMore: boolean;
  error: string;
  ready: boolean;
  note: string;
  // A batch has been asked for at least once, so 「沒有其他可以挑的款了」 is a fact and not a guess.
  primed: boolean;
  prime: () => void;
  loadMore: () => void;
  decide: (item: RestockV2ListPoolItem, decision: CounterListDecision) => void;
  pending: Record<string, boolean>;
}

export function useCounterPool(decideOne: PoolDecide): CounterPool {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<RestockV2ListPoolItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [totalRelevant, setTotalRelevant] = useState(0);
  const [filter, setFilterState] = useState<PoolFilter>({ scope: 'relevant', season: null, cat: null });
  const [rackGroups, setRackGroups] = useState<string[] | null>(null);
  const [facets, setFacets] = useState<RestockV2ListPoolFacets | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(true);
  const [note, setNote] = useState('');
  const [primed, setPrimed] = useState(false);
  // His answers, by SKU. Additive only — no landing plan, no refetch and no rollback of the shared
  // patch may clear an entry here. This is property 2 above, and it is the whole reason the set is
  // local to the pool instead of being derived from `listWrites.patch`.
  const [decided, setDecided] = useState<Record<string, true>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  // One batch at a time: a double tap on 看更多 would otherwise fetch the same offset twice and the
  // SKU dedupe would silently swallow the second page.
  const inFlight = useRef(false);

  const fetchBatch = useCallback(async (offset: number, first: boolean, view: PoolFilter) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (first) setLoading(true); else setLoadingMore(true);
    setError('');
    try {
      const response = await api.get<RestockV2ListPool>(POOL_URL, {
        // season / cat are OMITTED when 不分 — sending an empty value would be a filter on the
        // empty string, and 'ALL' is a season value (全季款) rather than 「no filter」.
        params: {
          offset, limit: POOL_BATCH, scope: view.scope,
          ...(view.season ? { season: view.season } : {}),
          ...(view.cat ? { cat: view.cat } : {}),
        },
      });
      const data = response?.data || ({} as RestockV2ListPool);
      setReady(data.ready !== false);
      setNote((data.note || '').trim());
      setTotal(Number(data.total) || 0);
      setTotalAll(Number(data.total_all) || Number(data.total) || 0);
      // Server truth first: `total_default` is the default cut's count under the same filters and
      // arrives on EVERY response, so the inactive chip stops printing a remembered number that
      // goes stale as soon as he decides something while on 全部. The remembered value is only the
      // fallback for a backend that does not send it.
      if (data.total_default !== null && data.total_default !== undefined) {
        setTotalRelevant(Number(data.total_default) || 0);
      } else if (view.scope === 'relevant') {
        setTotalRelevant(Number(data.total) || 0);
      }
      setRackGroups(Array.isArray(data.rack_groups) && data.rack_groups.length ? data.rack_groups : null);
      setFacets(data.facets || null);
      // `next_offset` null = exhausted. `has_more` is the server's own word for it; trust it over
      // arithmetic on lengths, which a mid-scroll pool_version bump would make wrong.
      const next = data.next_offset;
      setNextOffset(data.has_more === false || next === null || next === undefined ? null : Number(next));
      setItems((old) => mergePoolItems(first ? [] : old, poolItems(data)));
      setPrimed(true);
    } catch (err) {
      setError(readNokeError(err, POOL_ERROR));
      setPrimed(true);
    } finally {
      inFlight.current = false;
      if (first) setLoading(false); else setLoadingMore(false);
    }
  }, []);

  /**
   * Load the first batch WITHOUT opening the section. The strip can then print the honest count
   * (「還有 214 款」) before he commits to a wall of cards, and the tap that opens it is instant.
   * One request, POOL_BATCH rows — never the hundreds the pool may hold.
   */
  const prime = useCallback(() => {
    if (primed || loading || inFlight.current) return;
    fetchBatch(0, true, filter);
  }, [fetchBatch, primed, loading, filter]);

  const loadMore = useCallback(() => {
    if (error && !items.length) { fetchBatch(0, true, filter); return; }
    if (nextOffset === null) return;
    fetchBatch(nextOffset, false, filter);
  }, [error, items.length, nextOffset, fetchBatch, filter]);

  /**
   * The ONE way any filter changes — scope, season or category.
   *
   * A different filter is a different query, so the rows are DROPPED and the list restarts at
   * offset 0: keeping them would strand rows from the previous cut among the new ones and no
   * dedupe could tell them apart. His ANSWERS are not a query, so `decided` is untouched — a style
   * he rejected under one filter can never reappear under another.
   */
  const setFilter = useCallback((next: Partial<PoolFilter>) => {
    if (inFlight.current) return;
    const merged: PoolFilter = { ...filter, ...next };
    if (merged.scope === filter.scope && merged.season === filter.season && merged.cat === filter.cat) return;
    setFilterState(merged);
    setItems([]);
    setNextOffset(0);
    fetchBatch(0, true, merged);
  }, [filter, fetchBatch]);

  const clearFilter = useCallback(() => {
    setFilter({ season: null, cat: null });
  }, [setFilter]);

  const toggle = useCallback(() => {
    setOpen((old) => {
      if (!old) prime();
      return !old;
    });
  }, [prime]);

  /**
   * 加入清單 / 不要這款 on one pool row. Identical to the proposal card's decision — same endpoint,
   * same body, same stored 依據 — so a style added from here is indistinguishable from one added
   * from 建議加入, which is the point: the row is below the ENGINE's bar, not below his.
   *
   * Hide first, POST behind it, and put the row back only if the server refuses. Not awaited: the
   * click handler returns immediately (property 1).
   */
  const decide = useCallback((item: RestockV2ListPoolItem, decision: CounterListDecision) => {
    const sku = item.sku;
    setDecided((old) => ({ ...old, [sku]: true }));
    setPending((old) => ({ ...old, [sku]: true }));
    decideOne(sku, decision, item.tier ?? null, poolEvidence(item)).then((ok) => {
      if (!ok) setDecided((old) => { const next = { ...old }; delete next[sku]; return next; });
      setPending((old) => { const next = { ...old }; delete next[sku]; return next; });
    });
  }, [decideOne]);

  const decidedCount = Object.keys(decided).length;

  return {
    open,
    toggle,
    items: items.filter((item) => !decided[item.sku]),
    remaining: poolRemaining(total, decidedCount),
    totalAll,
    totalRelevant,
    scope: filter.scope,
    season: filter.season,
    cat: filter.cat,
    setFilter,
    clearFilter,
    filtered: filter.season !== null || filter.cat !== null,
    rackGroups,
    facets,
    hasMore: nextOffset !== null,
    loading,
    loadingMore,
    error,
    ready,
    note,
    primed,
    prime,
    loadMore,
    decide,
    pending,
  };
}

export default useCounterPool;
